'use client';

import { useEffect, useState, Suspense } from 'react';
import { MailCheck } from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { authApi } from '@/lib/api/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useBotProtection } from '@/lib/hooks/use-bot-protection';
import AuthLayout from '@/components/auth/auth-layout';
import { isEmailTakenError, storePendingClaimToken } from '@/lib/shared-link-claim';
import { apiErrorMessage } from '@/lib/api/errors';

function RegisterContent() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const { isReady, honeypotProps, getBotProtection, refresh: refreshBotProtection } = useBotProtection();
  // Set once the account exists: the page swaps the form for a "check your inbox" panel
  // instead of dropping a brand-new user on a "Welcome back" sign-in screen.
  const [registeredEmail, setRegisteredEmail] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const hasClaim = searchParams.get('claim') !== null;

  // Anonymous short links created on the website arrive here with ?claim=...
  // Persist the token so it survives the register → verify-email → auto-login journey.
  useEffect(() => {
    const claimToken = searchParams.get('claim');
    if (claimToken) {
      storePendingClaimToken(claimToken);
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await authApi.register({
        email,
        password,
        tenant_name: tenantName.trim() || undefined,
        bot_protection: getBotProtection(),
      });
      toast.dismiss(); // an error toast from a failed first attempt would sit over the success panel
      setRegisteredEmail(email);
      // A resend needs a fresh proof-of-work: the backend spends a challenge per attempt.
      refreshBotProtection();
    } catch (error) {
      // Same reason: without a new challenge, every retry after a typo failed with
      // "Invalid challenge solution" until the page was reloaded.
      refreshBotProtection();
      // The dead end that stranded anonymous links: the website's only route out
      // of the promotion screen was "create an account", so an address that
      // already had one lost the link here. The token is still in sessionStorage,
      // so signing in redeems it.
      if (isEmailTakenError(error)) {
        toast.error('That email already has an account', {
          description: hasClaim
            ? "Sign in instead — we'll add your short link to that workspace."
            : 'Sign in instead.',
          action: {
            label: 'Sign in',
            // claimPending, never claim: the real token is already in
            // sessionStorage from this page's mount, and a placeholder in
            // `claim` would overwrite it on arrival.
            onClick: () =>
              router.push(hasClaim ? '/auth/login?claimPending=1' : '/auth/login'),
          },
        });
      } else {
        toast.error(apiErrorMessage(error, 'Registration failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (!registeredEmail) return;
    setResending(true);
    try {
      const response = await authApi.resendVerification({
        email: registeredEmail,
        bot_protection: getBotProtection(),
      });
      toast.success(response.message);
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Could not resend the e-mail'));
    } finally {
      refreshBotProtection();
      setResending(false);
    }
  };

  if (registeredEmail) {
    return (
      <AuthLayout>
        <div className="space-y-6 text-center">
          <MailCheck className="mx-auto h-10 w-10 text-primary" aria-hidden="true" />
          <div className="space-y-2">
            <h1 className="text-3xl font-bold tracking-tight">Check your inbox</h1>
            <p className="text-muted-foreground">
              We sent a confirmation link to <strong className="text-foreground">{registeredEmail}</strong>.
              Open it to finish setting up your account.
            </p>
            {hasClaim && (
              <p className="text-sm text-muted-foreground">
                Your short link is waiting: it moves into your workspace as soon as you confirm.
              </p>
            )}
          </div>
          <div className="space-y-3">
            <Button
              variant="outline"
              className="w-full h-11"
              onClick={handleResend}
              disabled={resending || !isReady}
            >
              {resending ? 'Sending…' : 'Resend the e-mail'}
            </Button>
            <p className="text-sm text-muted-foreground">
              Wrong address?{' '}
              <button
                type="button"
                className="text-primary hover:underline font-medium"
                onClick={() => setRegisteredEmail(null)}
              >
                Start over
              </button>
              {' '}·{' '}
              <Link href="/auth/login" className="text-primary hover:underline font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">Create your account</h1>
        <p className="text-muted-foreground">
          Short links and smart routing, on your own domain
        </p>
        {hasClaim && (
          <p className="text-sm text-muted-foreground">
            Your short link will be added to your workspace automatically.
          </p>
        )}
      </div>

      {/* Register form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              className="h-11"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              minLength={8}
              className="h-11"
            />
            <p className="text-xs text-muted-foreground">
              Must be at least 8 characters
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="tenant">
              Workspace name <span className="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="tenant"
              type="text"
              placeholder="Your company or team"
              value={tenantName}
              onChange={(e) => setTenantName(e.target.value)}
              disabled={loading}
              className="h-11"
            />
          </div>
        </div>

        {/* Honeypot field - hidden from real users */}
        <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            {...honeypotProps}
          />
        </div>

        <Button type="submit" className="w-full h-11" disabled={loading || !isReady}>
          {loading ? 'Creating account...' : 'Create Account'}
        </Button>

        <p className="text-sm text-center text-muted-foreground">
          Already have an account?{' '}
          <Link href="/auth/login" className="text-primary hover:underline font-medium">
            Sign in
          </Link>
          {' '}or{' '}
          <Link href="/auth/forgot-password" className="text-primary hover:underline font-medium">
            forgot password?
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}

export default function RegisterPage() {
  return (
    <Suspense>
      <RegisterContent />
    </Suspense>
  );
}
